import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 group"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-file-list-3-line text-background-50 text-lg"></i>
            </div>
            <span className={`font-heading font-semibold text-lg md:text-xl whitespace-nowrap transition-colors ${scrolled ? 'text-foreground-900' : 'text-background-50'}`}>
              cssspreadsheet<span className="text-primary-500">.quest</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium transition-colors hover:text-primary-500 whitespace-nowrap ${scrolled ? 'text-foreground-700' : 'text-background-50/90'}`}
            >
              Home
            </a>
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-primary-500 whitespace-nowrap ${scrolled ? 'text-foreground-700' : 'text-background-50/90'}`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-56 bg-background-50 rounded-xl border border-background-200/80 py-2 shadow-lg">
                  <div className="max-h-72 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <i className={`${cat.icon} text-base text-primary-500`}></i>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium transition-colors hover:text-primary-500 whitespace-nowrap ${scrolled ? 'text-foreground-700' : 'text-background-50/90'}`}
            >
              About
            </a>
            <Link
              to="/cssbuy-sheet-guide"
              className={`text-sm font-medium transition-colors hover:text-primary-500 whitespace-nowrap ${scrolled ? 'text-foreground-700' : 'text-background-50/90'}`}
            >
              Guide
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center px-5 py-2.5 bg-primary-500 text-background-50 font-label font-semibold text-sm rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              cssbuy sheet
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center"
            >
              <i className={`text-xl ${scrolled ? 'text-foreground-800' : 'text-background-50'} ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-4 py-4 space-y-2">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="block py-3 px-4 text-foreground-700 text-sm font-medium hover:bg-primary-50 rounded-lg">Home</a>
            <div>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full flex items-center justify-between py-3 px-4 text-foreground-700 text-sm font-medium hover:bg-primary-50 rounded-lg"
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="pl-4 max-h-60 overflow-y-auto">
                  {categories.map((cat) => (
                    <a key={cat.name} href={cat.url} target="_blank" rel="noopener nofollow" className="flex items-center gap-2 py-2 px-4 text-sm text-foreground-600 hover:text-primary-600 rounded-lg">
                      <i className={`${cat.icon} text-primary-500`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="block py-3 px-4 text-foreground-700 text-sm font-medium hover:bg-primary-50 rounded-lg">About</a>
            <Link to="/cssbuy-sheet-guide" className="block py-3 px-4 text-foreground-700 text-sm font-medium hover:bg-primary-50 rounded-lg">Guide</Link>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="block py-3 px-4 mt-2 text-center bg-primary-500 text-background-50 font-label font-semibold text-sm rounded-full">
              cssbuy sheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}